import { ref } from "vue";
import { Device } from "mediasoup-client";
import type {
  DtlsParameters,
  Transport,
  Producer,
  RtpCapabilities,
  MediaKind
} from "mediasoup-client/types";

import { useVideoHub } from "@/composables/useVideoHub";
import { producerOptionsVideo } from "@/config";
import { selectSendVideoCodec } from "@/services/videoCodecSelection";
import { CameraState } from "@/enums/CameraState";
import { VideoState } from "@/types/VideoState";

export const videoState = ref<VideoState>(VideoState.NeverConnected);

export function useVideoCall(
  videoId: string
) {

  const videoHub = useVideoHub(videoId);

  let device: Device | undefined;
  let rtpCapabilities: RtpCapabilities | undefined;

  let sendTransport: Transport | undefined;
  let videoProducer: Producer | undefined;
  let statsIntervalId: number | undefined;
  let statsTimeoutId: number | undefined;
  let intentionalTrackStop = false;

  const STATS_COLLECTION_INTERVAL_MS = 5000; // 5 seconds
  const STATS_COLLECTION_DURATION_MS = 60000; // 1 minute

  async function startCall(track: MediaStreamTrack) {
    try {
      setCameraState(CameraState.Enabled); // enabled by default

      videoState.value = VideoState.Connecting;

      const connection = await videoHub.startConnection();

      connection.on("CallerProducerReplaced", () => {
        console.warn("[useVideoCall] Producer replaced by another device");
        videoState.value = VideoState.ReplacedByAnotherDevice;
        cleanup();
      });

      connection.onreconnected(() => {
        console.log("[useVideoCall] SignalR reconnected after network change, reloading page");
        window.location.reload();
      });

      const joinResult = await videoHub.joinRoom();

      console.log("Join Result:", joinResult);

      rtpCapabilities = joinResult?.routerRtpCapabilities;
      if (!rtpCapabilities) {
        throw new Error("Missing RTP capabilities");
      }

      await createDevice();
      await createSendTransport();
      await produceVideo(track);

    } catch (err) {
      console.error("startCall failed", err);
      if (videoState.value !== VideoState.UnsupportedBrowser) {
        videoState.value = VideoState.Failed;
      }
      cleanup();
      throw err;
    }
  }

  async function endCall() {
    await videoHub.disconnect();
    cleanup();
    videoState.value = VideoState.Closed;
  }

  async function createDevice() {
    try {
      device = new Device();

      await device.load({ routerRtpCapabilities: rtpCapabilities! });
    } catch (err: any) {
      if (err?.name === "UnsupportedError") {
        videoState.value = VideoState.UnsupportedBrowser;
      }
      throw err;
    }
  }

  async function createSendTransport() {
    if (!device) {
      throw new Error("Device not initialized");
    }

    const params = await videoHub.createWebRtcTransport();

    sendTransport = device.createSendTransport(params);

    sendTransport.on(
      "connect",
      async ({ dtlsParameters }, callback: any, errback: any) => {
        try {
          await videoHub.connectWebRtcTransport(dtlsParameters);
          callback();
        } catch (err) {
          errback(err as Error);
        }
      }
    );

    sendTransport.on("produce", async (parameters: any, callback: any, errback: any) => {
      try {
        // Video orientation is derived server-side from the in-band video-orientation (CVO)
        // RTP extension, so all consumers (live view, recording, analysis) stay in sync.
        const producerId = await videoHub.produce(
          parameters.kind as MediaKind,
          parameters.rtpParameters
        );

        if (!producerId) {
          throw new Error("Producer ID not returned");
        }

        callback({ id: producerId });
      } catch (err) {
        errback(err as Error);
      }
    });

    sendTransport.on("connectionstatechange", (state: RTCPeerConnectionState) => {
      switch (state) {
        case "connecting":
          videoState.value = VideoState.Connecting;
          break;
        case "connected":
          videoState.value = VideoState.Connected;
          break;
        case "failed":
          videoState.value = VideoState.Failed;
          cleanup();
          break;
        case "disconnected":
          videoState.value = VideoState.Disconnected;
          break;
        case "closed":
          videoState.value = VideoState.Closed;
          cleanup();
          break;
      }
    });
  }

  async function produceVideo(track: MediaStreamTrack) {
    if (!sendTransport || !device) {
      throw new Error("Send transport not ready");
    }

    // Dynamic emergency scene: bias the encoder toward smooth motion. MediaService already sets
    // this on the capture track, but set it here too so any track reaching the encoder is hinted.
    track.contentHint = "motion";

    // Send the best codec this phone can actually ENCODE (H264 preferred, VP8 floor). Matching H264
    // on profile (not level) lets an iOS/Safari caller — which encodes only Constrained Baseline —
    // select the router's CB@Level 5.0 codec, so it isn't pinned to a 720p-class level.
    const selectedCodec = selectSendVideoCodec(device.rtpCapabilities);

    console.log(`Producing video with codec ${selectedCodec.mimeType}`);

    videoProducer = await sendTransport.produce({
      track,
      ...producerOptionsVideo,
      codec: selectedCodec
    });

    await applyDegradationPreference(videoProducer);

    videoProducer.on("trackended", () => {
      console.warn("Video track ended");
      if (!intentionalTrackStop) {
        cleanup();
      }
    });

    videoProducer.on("transportclose", () => {
      console.warn("Producer transport closed");
      cleanup();
    });

    startStatsCollection();
  }

  // Under a constrained uplink trade BOTH resolution and frame rate ("balanced"), so a dynamic
  // scene (hand shake, panning) degrades smoothly instead of clinging to 1080p and smearing.
  // "maintain-resolution" would keep 1080p AND disable libwebrtc's QP quality scaler, letting QP
  // climb unbounded (the classic blur). A lower resolution at a low bitrate has lower QP = sharper
  // pixels. The libwebrtc default differs between versions, so set it explicitly after produce().
  // See Docs/External/WebRTC/webrtc-video-core-concepts.md §3.
  async function applyDegradationPreference(producer: Producer) {
    const sender = producer.rtpSender;
    if (!sender) {
      return;
    }
    try {
      const params = sender.getParameters() as RTCRtpSendParameters & {
        degradationPreference?: RTCDegradationPreference;
      };
      params.degradationPreference = "balanced";
      await sender.setParameters(params);
    } catch (err) {
      console.warn("Failed to set degradationPreference", err);
    }
  }

  async function replaceVideoTrack(track: MediaStreamTrack) {
    if (!videoProducer) {
      return;
    }

    track.contentHint = "motion"; // keep the motion bias across a camera switch
    await videoProducer.replaceTrack({ track });
  }

  async function pauseProducer() {
    await setCameraState(CameraState.Disabled);
    videoProducer?.pause();
    videoState.value = VideoState.Paused;
  }

  async function resumeProducer() {
    videoProducer?.resume();
    await setCameraState(CameraState.Enabled);
    videoState.value = VideoState.Connected;
  }

  async function setCameraState(state: CameraState) {
    await videoHub.setCameraState(state);
  }

  function startStatsCollection() {
    if (statsIntervalId) return;

    console.log("Producer stats collection started (1 minute)");

    statsIntervalId = window.setInterval(async () => {
      if (sendTransport) {
        try {
          const stats = await (sendTransport as any)._handler._pc.getStats();
          const statsObj: any = {};
          stats.forEach((report: any) => {
            statsObj[report.id] = report;
          });
          await videoHub.sendProducerStats(statsObj);
        } catch (err) {
          console.error("Failed to collect producer stats:", err);
        }
      }
    }, STATS_COLLECTION_INTERVAL_MS);

    // Stop after 1 minute
    statsTimeoutId = window.setTimeout(() => {
      console.log("Producer stats collection timeout reached");
      stopStatsCollection();
    }, STATS_COLLECTION_DURATION_MS);
  }

  function stopStatsCollection() {
    if (statsIntervalId) {
      clearInterval(statsIntervalId);
      statsIntervalId = undefined;
      console.log("Producer stats collection stopped");
    }

    if (statsTimeoutId) {
      clearTimeout(statsTimeoutId);
      statsTimeoutId = undefined;
    }
  }

  function cleanup() {
    stopStatsCollection();
    try { videoProducer?.close(); } catch {}
    try { sendTransport?.close(); } catch {}

    device = undefined;
    sendTransport = undefined;
    videoProducer = undefined;
    rtpCapabilities = undefined;
  }

  function setIntentionalTrackStop(value: boolean) {
    intentionalTrackStop = value;
  }

  function isActive(): boolean {
    return !!videoProducer && !!sendTransport;
  }

  return {
    startCall,
    endCall,
    replaceVideoTrack,
    pauseProducer,
    resumeProducer,
    setIntentionalTrackStop,
    isActive,
    videoState
  };
}
